import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { ErrorType, recordError } from '../../../models/apiWrappers/api/exceptions';
import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { generateRandomNumber } from '../../../utils/dataGenerators/generateRandomNumber';
import { ThreeDotsLoader } from '../../common/ThreeDotsLoader';
import { BaseStepV2 } from '../../common/v2/BaseStepV2';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { ContentBox } from '../../common/v2/Box/Box.style';
import {
  ThanksForSharingLoaderDescription,
  ThanksForSharingLoaderTitle,
  ThanksForSharingLoaderWrapper,
} from './ThanksForSharingLoaderStep.style';

interface ThanksForSharingLoaderStepProps extends BaseStepPropsV2 {
  loadOnboardingGoals?: boolean;
}

export const ThanksForSharingLoaderStep: React.FC<ThanksForSharingLoaderStepProps> = observer((props) => {
  const { isActive, nextStep, title, description } = props;
  const analytics = useAnalytics();
  const {
    Onboarding: { fetchAndSetOnboardingGoals },
    UserDiscoveryResponse,
  } = useMst();

  // even if we load real data, we still want to show the screen
  // even for a short period of time
  const goToNextStepInMs = generateRandomNumber(1000, 2000);

  const fetchOnboardingGoals = useCallback(async () => {
    try {
      if (UserDiscoveryResponse.segment) {
        await fetchAndSetOnboardingGoals(UserDiscoveryResponse.planLabelsIdsFromAnswers, UserDiscoveryResponse.segment);
      }
    } catch (error) {
      recordError(ErrorType.API, 'Failed to fetch onboarding goals', { error });
    } finally {
      nextStep();
    }
  }, [
    UserDiscoveryResponse.segment,
    UserDiscoveryResponse.planLabelsIdsFromAnswers,
    fetchAndSetOnboardingGoals,
    nextStep,
  ]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isActive) {
      analytics.track(`view thanks for sharing`);
      window.scrollTo(0, 0);
      timeoutId = setTimeout(async () => {
        await fetchOnboardingGoals();
      }, goToNextStepInMs);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isActive, goToNextStepInMs, nextStep, analytics, fetchOnboardingGoals]);

  return (
    <BaseStepV2 {...props} addSpacingNeededForMobileButton={false}>
      <ThanksForSharingLoaderWrapper>
        <ContentBox>
          <ThanksForSharingLoaderTitle>{title}</ThanksForSharingLoaderTitle>
          <ThreeDotsLoader />
          <ThanksForSharingLoaderDescription>{description}</ThanksForSharingLoaderDescription>
        </ContentBox>
      </ThanksForSharingLoaderWrapper>
    </BaseStepV2>
  );
});
